/**
 * @file CountersPanel.tsx
 * @description Панель «Весь сайт → Счётчики и cookie» конструктора сайта
 * (phase19.16, партия Р3г, v0.19.62; site-builder.md SB133, SB134, SB137;
 * макет uiux-r3.md §3.6 «с канвой: баннер на канве»).
 *
 * Пишет две части черновика с автосохранением: COUNTERS (номера, режим,
 * уведомление без счётчиков, вступление, редакция текстов платформы —
 * `useSiteDraftPart("counters")`) и раздел темы `consentBanner` (вид
 * баннера — часть THEME, переносится дизайнами, SB94). Обе — право настроек
 * магазина (SB55); без права панели нет — объяснение в `WholeSitePanel`.
 * Признаки сохранения — `saveStatesToShow`: при двух бедах — обе строки.
 *
 * Канва показывает баннер, пока панель открыта (`useCanvasOverlays` в
 * `BuilderCanvas`); переключатель «Баннер / Окно «Настроить»» — вкладка
 * раздела в адресе (`panel=counters.settings`), виден, когда у баннера есть
 * «Настроить» (режимы «после согласия» и «Уведомление»).
 */

import { siteConsentBannerKind, type SiteConsentBanner } from "@chaiberi/shared";
import { useCallback } from "react";

import { NoticeStrip } from "../../ui/NoticeStrip";
import { Skeleton } from "../../ui/skeleton";
import { SaveIndicator } from "../SaveIndicator";
import { Segmented } from "../Segmented";
import { useSiteView } from "../site-view-context";
import { UnsavedGuard } from "../UnsavedGuard";
import { CountersFields } from "./CountersFields";
import { saveStatesToShow } from "./save-states";
import { useSiteDraftPart } from "../../../hooks/site-view/useSiteDraftParts";
import { useThemePreview } from "../../../hooks/site-view/useThemePreview";
import { consentCanvasView, hasConsentSettings } from "../../../lib/site-view/consent-platform";
import { consentBannerOf } from "../../../lib/site-view/counters-form";

interface Props {
  /** Вкладка раздела из адреса: что показывает канва. */
  canvasView: string | null;
  onCanvasView: (next: string | null) => void;
}

const CANVAS_VIEWS = [
  { value: "banner" as const, label: "Баннер" },
  { value: "settings" as const, label: "Окно «Настроить»" },
];

export function CountersPanel(props: Props) {
  const view = useSiteView();
  if (!view.parts) return <CountersSkeleton />;
  return <CountersBody {...props} />;
}

function CountersBody({ canvasView, onCanvasView }: Props) {
  const part = useSiteDraftPart("counters");
  const themeDraft = useSiteDraftPart("theme");
  const { theme, update: updateTheme } = themeDraft;
  const banner = consentBannerOf(theme);
  const showOnCanvas = useThemePreview(theme);
  // Раздел целиком: у темы без раздела запись всё равно полная.
  const updateBanner = useCallback((patch: Partial<SiteConsentBanner>) => updateTheme("consentBanner", { ...banner, ...patch }), [updateTheme, banner]);
  const previewBanner = useCallback((patch: Partial<SiteConsentBanner>) => showOnCanvas("consentBanner", { ...banner, ...patch }), [showOnCanvas, banner]);
  const kind = siteConsentBannerKind(part.counters, part.noticeAllowed);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <p className="type-caption text-on-surface-variant">
        Счётчики посещений и баннер согласия на cookie. Покупатели увидят после «Опубликовать».
      </p>
      {saveStatesToShow([part, themeDraft]).map((state, i) => (
        <SaveIndicator key={i} state={state} />
      ))}
      {part.notice ? <NoticeStrip tone="warning">{part.notice}</NoticeStrip> : null}
      {hasConsentSettings(kind) ? (
        <div className="flex min-w-0 shrink-0 flex-col gap-2">
          <span className="type-caption text-on-surface-variant">На образце сайта</span>
          <Segmented
            aria-label="Что показать на образце сайта"
            options={CANVAS_VIEWS}
            value={consentCanvasView(canvasView, kind)}
            onChange={(next) => onCanvasView(next === "banner" ? null : next)}
          />
        </div>
      ) : kind === null ? (
        <p className="type-caption text-on-surface-variant">Баннера на сайте не будет: счётчики не подключены и уведомление без них выключено.</p>
      ) : null}
      <CountersFields
        counters={part.counters}
        banner={banner}
        theme={theme}
        platform={part.platform}
        noticeAllowed={part.noticeAllowed}
        onChange={part.update}
        onBannerChange={updateBanner}
        onBannerPreview={previewBanner}
      />
      <UnsavedGuard when={[part.status, themeDraft.status].some((s) => s === "error" || s === "conflict")} />
    </div>
  );
}

function CountersSkeleton() {
  return (
    <div className="flex min-w-0 flex-col gap-4" aria-busy="true">
      <p className="type-caption text-on-surface-variant">Загружаем счётчики и баннер…</p>
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-16 rounded-xl" />
      ))}
    </div>
  );
}
